import { getClientFacingMessage, getSupportReference } from '@massilia/queries'
import {
  AlertCircle,
  AlertTriangle,
  Mail,
  Phone,
  ServerCrash,
  TimerOff,
  TrendingDown,
  TrendingUp,
  WifiOff,
  Wrench,
} from 'lucide-react'

import type { ClientErrorType } from '@/lib/client-error'

import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface ClientErrorStateProps {
  /** Les boutons, dans l'ordre : le premier est l'action principale. */
  actions?: React.ReactNode
  /** L'erreur d'origine : son message rédigé et sa référence en sortent. */
  error?: Error | null
  /** Écart de tarif en centimes, pour `price-changed` uniquement. */
  priceDriftCents?: number
  type: ClientErrorType
  /** `page` pour une frontière plein écran, `inline` dans une sheet ou une carte. */
  variant?: 'inline' | 'page'
}

const ICONS = {
  'booking-uncertain': AlertTriangle,
  'network': WifiOff,
  'price-changed': TrendingUp,
  'provider-down': Wrench,
  'server-error': ServerCrash,
  'session-expired': TimerOff,
  'unknown': AlertCircle,
} satisfies Record<ClientErrorType, typeof AlertCircle>

const TITLES = {
  'booking-uncertain': 'Réservation à vérifier avec nous',
  'network': 'Nous n’avons pas pu joindre le site',
  'price-changed': 'Le tarif de ce vol a changé',
  'provider-down': 'Notre partenaire ne répond pas',
  'server-error': 'Une panne de notre côté',
  'session-expired': 'Votre recherche a expiré',
  'unknown': 'Une erreur est survenue',
} satisfies Record<ClientErrorType, string>

/**
 * L'excuse, toujours présente et propre à chaque cas.
 *
 * Elle vit à part de la description parce que celle-ci cède parfois la place au
 * message rédigé par le serveur : sans cette ligne, l'excuse disparaîtrait
 * précisément sur les pannes, là où elle est due.
 */
const APOLOGIES = {
  'booking-uncertain': 'Désolés, nous préférons vérifier avec vous.',
  'network': 'Désolés, la connexion n’a pas abouti.',
  'price-changed': 'Désolés pour ce contretemps.',
  'provider-down': 'Désolés pour l’attente.',
  'server-error': 'Désolés, cette panne vient de chez nous.',
  'session-expired': 'Désolés pour ce contretemps.',
  'unknown': 'Désolés pour ce contretemps.',
} satisfies Record<ClientErrorType, string>

/** Ce qui s'est passé, et la sortie. Sans excuse : {@link APOLOGIES} s'en charge. */
const DESCRIPTIONS = {
  'booking-uncertain':
    'Notre partenaire n’a pas confirmé votre réservation. Aucun montant n’a été débité, mais ne la relancez pas : contactez-nous, nous vérifions l’état de votre dossier.',
  'network': 'Vérifiez votre connexion, puis réessayez dans quelques instants.',
  'price-changed':
    'Relancez votre recherche pour obtenir le tarif du moment et les conditions qui vont avec.',
  'provider-down':
    'Notre partenaire de réservation ne répond pas pour le moment. Aucun montant n’a été débité : réessayez dans quelques instants.',
  'server-error': 'Nos équipes en sont averties. Réessayez dans quelques instants.',
  'session-expired':
    'Les tarifs de cette recherche ne sont plus garantis. Relancez-la pour repartir des prix et des disponibilités du moment.',
  'unknown': 'Réessayez dans quelques instants. Si cela se reproduit, contactez-nous.',
} satisfies Record<ClientErrorType, string>

/**
 * Les cas où le serveur écrit mieux que nous.
 *
 * Sur une panne, son message nomme le prestataire, l'état du dossier, ce qui a
 * été débité — un détail que nous ne pouvons pas deviner ici. Sur une session
 * expirée ou un tarif qui bouge, l'inverse : il rend « Session expirée. Veuillez
 * relancer votre recherche. », plus pauvre que notre propre copie.
 *
 * Le message ne sort jamais brut : `getClientFacingMessage` écarte les textes
 * écrits pour un développeur.
 */
const PREFER_SERVER_MESSAGE = {
  'booking-uncertain': true,
  'network': false,
  'price-changed': false,
  'provider-down': true,
  'server-error': true,
  'session-expired': false,
  'unknown': true,
} satisfies Record<ClientErrorType, boolean>

/** Les pannes en rouge ; ce qui a simplement vieilli en orange. */
const TONES = {
  'booking-uncertain': 'danger',
  'network': 'danger',
  'price-changed': 'warning',
  'provider-down': 'danger',
  'server-error': 'danger',
  'session-expired': 'warning',
  'unknown': 'danger',
} satisfies Record<ClientErrorType, 'danger' | 'warning'>

const DISC_CLASSES = {
  danger: 'bg-danger-subtle text-danger-foreground',
  warning: 'bg-warning-subtle text-warning-foreground',
} satisfies Record<'danger' | 'warning', string>

/**
 * L'écran d'erreur du site public, dans sa seule et unique forme.
 *
 * Trois frontières de route et la sheet des conditions réécrivaient chacune la
 * même anatomie — disque, titre, texte, boutons — et c'est cette duplication qui
 * a laissé les copies diverger : deux d'entre elles affichaient au client le
 * `error.message` d'axios, soit « Request failed with status code 500 ».
 *
 * L'appelant garde ce qui lui est propre : son chrome autour, et ses actions,
 * parce que la sortie dépend de la page (relancer une recherche, reprendre un
 * paiement, revenir à l'accueil) et non du type d'erreur.
 */
export function ClientErrorState({
  actions,
  error,
  priceDriftCents,
  type,
  variant = 'inline',
}: ClientErrorStateProps) {
  const isPage = variant === 'page'
  const Icon = type === 'price-changed' && isCheaper(priceDriftCents) ? TrendingDown : ICONS[type]
  const supportReference = error ? getSupportReference(error) : undefined

  return (
    <div className="flex flex-col items-center gap-6 py-12" role="alert">
      <div
        className={`flex items-center justify-center rounded-full ${DISC_CLASSES[TONES[type]]} ${
          isPage ? 'size-16' : 'size-14'
        }`}
      >
        <Icon className={isPage ? 'size-8' : 'size-7'} />
      </div>

      <div className={`flex flex-col gap-2 text-center ${isPage ? 'max-w-md' : 'max-w-sm'}`}>
        <h3 className={`font-semibold text-gray-light-900 ${isPage ? 'text-xl' : 'text-base'}`}>
          {TITLES[type]}
        </h3>
        <p className="text-sm leading-relaxed text-gray-light-500">{APOLOGIES[type]}</p>
        <p className="text-sm leading-relaxed text-gray-light-600">
          {describe(type, error, priceDriftCents)}
        </p>
      </div>

      {type === 'booking-uncertain' && <AgencyContacts />}

      {actions && <div className="flex flex-wrap items-center justify-center gap-3">{actions}</div>}

      {/*
        La référence n'accompagne qu'une panne : sur un refus métier elle
        n'apporte rien et brouille un message par ailleurs actionnable. C'est le
        tri que fait déjà `getSupportReference`.
      */}
      {supportReference && (
        <p className="text-xs text-gray-light-500">
          Référence à nous communiquer : <span className="font-mono">{supportReference}</span>
        </p>
      )}
    </div>
  )
}

/** Le téléphone et l'e-mail de l'agence, seule sortie quand relancer est dangereux. */
function AgencyContacts() {
  return (
    <div className="flex w-full max-w-sm flex-col gap-2 rounded-xl border border-danger/30 bg-danger-subtle px-5 py-4 text-sm text-danger-foreground">
      <span className="flex items-center justify-center gap-2">
        <Phone className="size-4 shrink-0" />
        <a className="font-medium underline" href={`tel:${AGENCY_PHONE.replaceAll(/\s/g, '')}`}>
          {AGENCY_PHONE}
        </a>
      </span>
      <span className="flex items-center justify-center gap-2">
        <Mail className="size-4 shrink-0" />
        <a className="font-medium underline" href={`mailto:${AGENCY_EMAIL}`}>
          {AGENCY_EMAIL}
        </a>
      </span>
    </div>
  )
}

/** Ce qui s'est passé : notre copie, ou celle du serveur quand elle vaut mieux. */
function describe(
  type: ClientErrorType,
  error: Error | null | undefined,
  priceDriftCents?: number
): string {
  const fallback =
    type === 'price-changed'
      ? `${describeDrift(priceDriftCents)} ${DESCRIPTIONS[type]}`
      : DESCRIPTIONS[type]

  if (!error || !PREFER_SERVER_MESSAGE[type]) return fallback

  return getClientFacingMessage(error, fallback)
}

/** La variation de tarif, chiffrée quand le serveur l'a donnée. */
function describeDrift(priceDriftCents?: number): string {
  if (priceDriftCents === undefined || priceDriftCents === 0) {
    return 'Son prix a bougé depuis votre recherche.'
  }

  const amount = formatPrice(centsToEuros(Math.abs(priceDriftCents)))
  return priceDriftCents > 0
    ? `Il a augmenté de ${amount} depuis votre recherche.`
    : `Il a baissé de ${amount} depuis votre recherche.`
}

const isCheaper = (priceDriftCents?: number): boolean =>
  priceDriftCents !== undefined && priceDriftCents < 0
